import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const SubContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/subscription/status`, { withCredentials: true });
      setSubscription(data.subscription);
    } catch (_) {
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const isPro = useMemo(() => {
    if (!subscription) return false;
    if (subscription.status !== 'active') return false;
    if (subscription.expires_at) {
      const exp = new Date(subscription.expires_at);
      if (exp < new Date()) return false;
    }
    return true;
  }, [subscription]);

  const refresh = useCallback(() => fetchStatus(), [fetchStatus]);

  const isPlus = useMemo(() => {
    if (!isPro || !subscription) return false;
    return subscription.tier === 'plus' || (subscription.plan || '').startsWith('plus_');
  }, [isPro, subscription]);

  const value = useMemo(() => ({ subscription, isPro, isPlus, loading, refresh }), [subscription, isPro, isPlus, loading, refresh]);

  return <SubContext.Provider value={value}>{children}</SubContext.Provider>;
}

export function useSubscription() {
  const ctx = useContext(SubContext);
  if (!ctx) throw new Error('useSubscription must be within SubscriptionProvider');
  return ctx;
}
